// Read-only detail view for an admin public test. Reached by clicking a row in
// the Public Tests list; the Edit button hands off to the existing edit form.
// See docs/FeatureDesigns/assignments-tests/admin-public-tests.md
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { QuestionSet } from '@shared-ui/question-set-types';
import { LatexText } from '@shared-ui/latex';
import { isDevEnvironment } from '@shared-ui/environment';
import { LoadingSpinner, BackButton } from '@shared-ui/components';
import { QuestionPreview } from '@shared-ui/question-preview';
import { Link } from 'react-router-dom';
import { buildQuestionDisplayText, formatMarks } from '@padcanvas/shared';
import { questionApi } from '../../api/questionApi';
import { questionSetApi } from '../../api/questionSetApi';

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function timeLimitLabel(seconds?: number): string {
  if (!seconds) return 'No limit';
  return `${Math.round(seconds / 60)} min`;
}

function dateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

/** One compact read-only field row — label on the left, value on the right. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="test-detail-row">
      <span className="test-detail-label">{label}</span>
      <span className="test-detail-value">{children}</span>
    </div>
  );
}

export function TestDetailView({ qs, canEdit = true, onEdit, onTogglePublish, publishing, onBack }: {
  qs: QuestionSet;
  /** Server-computed: may this caller modify the set? Same rule the write path
   *  enforces, so the buttons and the guard cannot disagree. Absent → editable,
   *  so this never hides an action that would actually succeed. */
  canEdit?: boolean;
  onEdit: () => void;
  onTogglePublish: () => void;
  publishing: boolean;
  onBack: () => void;
}) {
  // Public sets are authored on dev only; see the Edit button below.
  const editBlocked = !isDevEnvironment() && qs.visibility === 'public';
  const subjects = [...qs.subjects].sort().map(cap).join(', ') || '—';
  const classes = qs.classes?.join(', ') || qs.pypRef?.class || '—';
  // Topic exercises carry multi-exam targeting; exam papers a single exam. Empty = generic (all exams).
  const exam = qs.targetExams?.length
    ? qs.targetExams.join(', ')
    : (qs.targetExam || qs.pypRef?.exam || '—');

  // Read-only question list (no edit/actions). One batch request for content.
  const { data: byId, isLoading } = useQuery({
    queryKey: ['test-detail-questions', qs.id],
    queryFn: async () => {
      // A freshly-created PDF-import shell (or a legacy doc predating the
      // field) can carry no questionIds at all rather than an empty array.
      const { questions } = qs.questionIds?.length
        ? await questionApi.getPaperQuestions(qs.questionIds)
        : { questions: [] };
      return new Map(questions.map(q => [q.id, q]));
    },
  });

  // The one question the preview dialog is showing. Fetched on its own because
  // the list above carries no answer or solution — the dialog needs both.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const { data: previewQuestion, isLoading: previewLoading } = useQuery({
    queryKey: ['question-detail', previewId],
    queryFn: () => questionApi.getQuestionById(previewId!),
    enabled: !!previewId,
  });

  // P5: practice-lock — reserve this paper for tests (hide its questions from practice).
  const [locked, setLocked] = useState(!!qs.practiceLocked);
  const [locking, setLocking] = useState(false);
  const toggleLock = async () => {
    setLocking(true);
    try {
      const res = await questionSetApi.setPracticeLock(qs.id, !locked);
      setLocked(res.locked);
    } finally {
      setLocking(false);
    }
  };

  const hasSections = !!qs.sections?.length;
  const groups = hasSections
    ? qs.sections!
    : [{ id: '__all', title: undefined, questionIds: qs.questionIds, requiredCount: undefined }];
  let counter = 0;

  return (
    <div className="admin-tests-page">
      <BackButton label="list" onClick={onBack} />

      <div className="test-detail-header">
        <div className="test-detail-heading">
          <h1>{qs.title}</h1>
          <span className={`test-status test-status-${qs.status}`}>{qs.status}</span>
        </div>
        <div className="test-detail-actions">
          {!canEdit && (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              Read-only — this test belongs to another tenant
            </span>
          )}
          {canEdit && (
          <button className="btn-secondary" onClick={toggleLock} disabled={locking} title="Reserve this paper for tests — its questions are withheld from practice until a student attempts it">
            {locking ? '…' : locked ? '🔒 Practice-locked' : '🔓 Lock for tests'}
          </button>
          )}
          {canEdit && (
          <button className="btn-secondary" onClick={onTogglePublish} disabled={publishing}>
            {publishing ? '…' : qs.status === 'published' ? 'Unpublish' : 'Publish'}
          </button>
          )}
          {/* A public test is authored on dev and carried to prod by the
              export/import path, so that both environments hold the same set at
              the same version. Editing it here would fork prod's copy and the
              next import would silently overwrite the change — so on prod the
              Edit button is simply hidden. `canEdit` is the separate tenant
              gate: another tenant's test stays read-only either way. */}
          {canEdit && !editBlocked && (
            <button className="btn-primary" onClick={onEdit}>Edit</button>
          )}
        </div>
      </div>

      {qs.description && <p className="test-detail-desc">{qs.description}</p>}

      {/* Compact, read-only mirror of the edit form — same field order. PYP
          collapses to its single display string; marking is omitted here. */}
      <div className="test-detail-fields">
        {qs.pyqType === 'FullPYP' && qs.pypRef && <Row label="Previous Year Paper">{qs.pypRef.displayString}</Row>}
        <Row label="Target classes">{classes}</Row>
        <Row label="Target exam">{exam}</Row>
        <Row label="Subjects">{subjects}</Row>
        {qs.source && <Row label="Source">{qs.source}</Row>}
        <Row label="Time limit">{timeLimitLabel(qs.config.timeLimitSeconds)}</Row>
      </div>

      <div className="test-detail-meta">
        {(qs.type ?? 'test')} · {qs.visibility} · {qs.timesAttempted} attempt{qs.timesAttempted === 1 ? '' : 's'}
        {' · '}Created {dateTime(qs.createdAt)} · Updated {dateTime(qs.updatedAt)}
      </div>

      <section className="test-detail-section">
        <div className="test-detail-section-head">
          <h2>Questions ({qs.totalQuestions})</h2>
          {/* A new tab: this detail lives in the Tests page's state, so
              leaving it in place would lose it on the way back. */}
          <Link to={`/tests/${qs.id}/pdf`} target="_blank" className="btn-secondary test-detail-pdf-link">
            View in PDF
          </Link>
        </div>

        {isLoading ? (
          <div className="test-detail-qloading"><LoadingSpinner /></div>
        ) : (
          <div className="test-detail-qlist">
            {groups.map((g, gi) => (
              <div key={g.id} className="test-detail-qgroup">
                {hasSections && (
                  <h3 className="test-detail-qsection">
                    {g.title || `Section ${gi + 1}`}
                    {g.requiredCount != null && g.requiredCount < g.questionIds.length && (
                      <span className="test-detail-qsection-note"> — attempt any {g.requiredCount} of {g.questionIds.length}</span>
                    )}
                  </h3>
                )}
                {g.questionIds.map(qid => {
                  counter += 1;
                  const q = byId?.get(qid);
                  const isDraft = q?.importStatus === 'draft';
                  const marksLabel = q ? formatMarks(q.marks ?? 0) : null;
                  return (
                    <div
                      key={qid}
                      className={`test-detail-qrow${isDraft ? ' test-detail-qrow-draft' : ''}${q ? ' test-detail-qrow-clickable' : ''}`}
                      onClick={q ? () => setPreviewId(qid) : undefined}
                    >
                      <span className="test-detail-qnum">{counter}.</span>
                      <span className="test-detail-qtext">
                        {q
                          ? <LatexText text={buildQuestionDisplayText(q)} truncate={200} />
                          : <span className="test-detail-qmissing">Question unavailable</span>}
                      </span>
                      {marksLabel && <span className="test-detail-qmarks">{marksLabel}</span>}
                      {isDraft && <span className="test-detail-qdraft">Draft</span>}
                      {q?.questionType && q.questionType !== 'text' && (
                        <span className="test-detail-qtype">{q.questionType}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </section>

      {previewId && (
        <QuestionPreview
          question={previewQuestion ?? null}
          loading={previewLoading}
          onClose={() => setPreviewId(null)}
          renderFooter={(q) => (
            <Link
              // Carry the SET, not just the question: the list then shows every
              // question in this test with this one selected.
              to={`/questions?setId=${qs.id}&id=${q.id}`}
              className="qp-view-in-list-link"
            >
              View in Questions List
            </Link>
          )}
        />
      )}
    </div>
  );
}
